<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';

const router = useRouter();
const { accountId } = useAccount();

const selectedType = ref(null);
const isConnecting = ref(false);
const hasError = ref(false);
const errorMessage = ref('');

const connectionTypes = [
  {
    id: 'personal',
    title: 'LinkedIn Personal',
    description: 'Connect your personal LinkedIn profile to share updates and engage with your network',
    icon: 'i-ri-user-line',
    bgColor: '#0A66C2',
  },
  {
    id: 'page',
    title: 'LinkedIn Page',
    description: 'Connect your LinkedIn company or organization page to manage business content',
    icon: 'i-ri-building-line',
    bgColor: '#0A66C2',
  },
];

const connectLinkedIn = async (type) => {
  try {
    isConnecting.value = true;
    selectedType.value = type;

    // Call Chatwoot backend to get LinkedIn OAuth URL (Direct OAuth)
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/linkedin/authorization_url`,
      {
        params: { type }
      }
    );

    const { url } = response.data;

    if (!url) {
      hasError.value = true;
      errorMessage.value = 'Could not generate LinkedIn authorization URL';
      isConnecting.value = false;
      return;
    }

    // Redirect to LinkedIn OAuth (Direct)
    window.location.href = url;
  } catch (error) {
    hasError.value = true;
    errorMessage.value = error.response?.data?.error || error.message || 'Failed to connect to LinkedIn';
    isConnecting.value = false;
  }
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full">
      <!-- Error State -->
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5 class="text-xl font-semibold text-red-600 mb-2">Connection Error</h5>
        <p class="text-sm text-n-slate-11">{{ errorMessage }}</p>
      </div>

      <!-- LinkedIn Type Selection -->
      <div v-else class="w-full max-w-2xl">
        <div class="text-center mb-8">
          <h6 class="text-2xl font-medium mb-2">Connect LinkedIn</h6>
          <p class="text-sm text-n-slate-11">
            Choose the type of LinkedIn account you want to connect
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            v-for="type in connectionTypes"
            :key="type.id"
            class="relative group cursor-pointer"
            @click="connectLinkedIn(type.id)"
          >
            <div
              class="h-full p-6 rounded-2xl border-2 transition-all duration-300"
              :class="[
                selectedType === type.id && isConnecting
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-n-weak hover:border-blue-500 hover:shadow-lg'
              ]"
            >
              <!-- Icon -->
              <div
                class="w-16 h-16 mb-4 rounded-full flex items-center justify-center text-white"
                :style="{ backgroundColor: type.bgColor }"
              >
                <span :class="type.icon" class="text-3xl" />
              </div>

              <!-- Title & Description -->
              <h6 class="text-lg font-semibold mb-2">{{ type.title }}</h6>
              <p class="text-sm text-n-slate-11 mb-4">{{ type.description }}</p>

              <!-- Connect Button -->
              <button
                class="w-full flex items-center justify-center px-4 py-3 text-white rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                :style="{ backgroundColor: type.bgColor }"
                :disabled="isConnecting && selectedType === type.id"
                @click.stop="connectLinkedIn(type.id)"
              >
                <span v-if="isConnecting && selectedType === type.id" class="mr-2">
                  <svg
                    class="w-5 h-5 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </span>
                <span class="i-ri-linkedin-box-fill mr-2 text-xl" />
                <span class="font-medium">
                  {{ isConnecting && selectedType === type.id ? 'Connecting...' : 'Connect' }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <!-- Help Text -->
        <div class="mt-8 p-4 bg-blue-50 rounded-lg">
          <h6 class="text-sm font-medium text-blue-900 mb-2">
            What happens next?
          </h6>
          <ol class="text-xs text-blue-800 space-y-1 list-decimal list-inside">
            <li>You'll be redirected to LinkedIn to authorize the connection</li>
            <li>Grant the necessary permissions</li>
            <li>You'll be redirected back to complete the channel setup</li>
            <li>Add agents and start managing conversations</li>
          </ol>
        </div>
      </div>
    </div>
  </div>
</template>
